import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ApiService } from '@core/services/api/api.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';

export type TransactionViewSourceMode = 'POST_ID_MODULE' | 'POST_ROW' | 'GET_BY_ID';

export interface TransactionViewSourceConfig {
  mode: TransactionViewSourceMode;
  /** API endpoint path (without id for GET_BY_ID). */
  endpoint: string;
  /** When set, navigate here instead of resolving route from module_type. */
  fixedRoute?: string;
}

export interface TransactionViewSourceGridRef {
  showLoading(text: string): void;
  hideLoading(): void;
  handleError(error: unknown): void;
}

export interface TransactionViewSourceRow {
  id?: number | string;
  module_type?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class TransactionViewSourceService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly userEnv = inject(UserEnvService);

  /** Canonical module_type → form route map (legacy parity). */
  routeForModuleType(moduleType: string): string | null {
    switch (moduleType) {
      case 'PURCHASE_INVOICE':
        return '/purchaseManagement/movements/purchaseInvoiceList/form';
      case 'PURCHASE_DISPATCH_RECEIPT':
        return '/purchaseManagement/movements/purchaseDispatcheList/form';
      case 'SALES_INVOICE':
        return '/saleManagement/movements/saleInvoiceList/form';
      case 'SALES_DISPATCH_RECEIPT':
        return '/saleManagement/movements/saleDispatchList/form';
      case 'CURRENT_ACCOUNT_RECEIPT':
      case 'RETAIL_END_OF_DAY':
        return '/financeManagement/movements/currentAccountReceipts/form';
      case 'CASH_ACCOUNT':
        return '/financeManagement/movements/cashAccountReceipts/form';
      case 'PURCHASE_ORDER_RECEIPT':
        return '/purchaseManagement/movements/purchaseOrderList/form';
      case 'SALES_ORDER_RECEIPT':
        return '/saleManagement/movements/saleOrderList/form';
      case 'MATERIAL_RECEIPT':
        return '/materialManagement/movements/materialReceiptList/form';
      default:
        return null;
    }
  }

  open(
    config: TransactionViewSourceConfig,
    row: TransactionViewSourceRow,
    grid: TransactionViewSourceGridRef
  ): void {
    if (row?.id == null) {
      // order-transaction list DTO has no id — open by document_num
      if (this.openOrderByDocumentNum(row, grid)) return;
      grid.handleError('no_record_selected');
      return;
    }

    if (config.mode === 'POST_ID_MODULE') {
      if (!row.module_type) {
        grid.handleError('no_record_selected');
        return;
      }
      const route = config.fixedRoute ?? this.routeForModuleType(row.module_type);
      if (!route) {
        grid.handleError('error_data');
        return;
      }
      grid.showLoading('loading');
      this.api
        .addData<{ id?: number | string }>(config.endpoint, {
          id: row.id,
          module_type: row.module_type,
        })
        .pipe(finalize(() => grid.hideLoading()))
        .subscribe({
          next: (response) => {
            const sourceId = response?.id ?? row.id;
            void this.router.navigate([route], {
              queryParams: {
                id: String(sourceId),
                mode: 'view',
                moduleType: row.module_type,
              },
            });
          },
          error: (err) => grid.handleError(err),
        });
      return;
    }

    if (config.mode === 'POST_ROW') {
      const route =
        config.fixedRoute ?? '/financeManagement/movements/cashAccountReceipts/form';
      grid.showLoading('loading');
      this.api
        .addData<{ id?: number | string }>(config.endpoint, row as object)
        .pipe(finalize(() => grid.hideLoading()))
        .subscribe({
          next: (response) => {
            const sourceId = response?.id ?? row.id;
            void this.router.navigate([route], {
              queryParams: {
                id: String(sourceId),
                mode: 'view',
              },
            });
          },
          error: (err) => grid.handleError(err),
        });
      return;
    }

    if (config.mode === 'GET_BY_ID') {
      const route =
        config.fixedRoute ?? '/financeManagement/movements/bankAccountReceipts/form';
      grid.showLoading('loading');
      this.api
        .getDataById<{ id?: number | string }>(config.endpoint, row.id)
        .pipe(finalize(() => grid.hideLoading()))
        .subscribe({
          next: (response) => {
            const sourceId = (response as { id?: number | string })?.id ?? row.id;
            void this.router.navigate([route], {
              queryParams: {
                id: String(sourceId),
                mode: 'view',
              },
            });
          },
          error: (err) => grid.handleError(err),
        });
    }
  }

  /** When list row has no id: resolve purchase/sales order via document_num. */
  private openOrderByDocumentNum(
    row: TransactionViewSourceRow,
    grid: TransactionViewSourceGridRef
  ): boolean {
    const moduleType = row.module_type;
    const documentNum = row['document_num'];
    const listEndpoint =
      moduleType === 'PURCHASE_ORDER_RECEIPT'
        ? 'purchase-order'
        : moduleType === 'SALES_ORDER_RECEIPT'
          ? 'sales-order'
          : null;
    const route = moduleType ? this.routeForModuleType(moduleType) : null;
    if (typeof documentNum !== 'string' || !documentNum || !listEndpoint || !route) {
      return false;
    }

    const env = this.userEnv.env();
    grid.showLoading('loading');
    this.api
      .getFilteredData<{ id?: number | string }>(listEndpoint, {
        filter: {
          company: { id: env?.company?.id ?? null },
          working_period: { id: env?.working_period?.id ?? null },
          document_num: documentNum,
        },
        query: { paging: { limit: 1, offset: 0, fetch: 'EAGER' } },
        parameters: [],
      })
      .pipe(finalize(() => grid.hideLoading()))
      .subscribe({
        next: (response) => {
          const found = (response as { data?: { id?: number | string }[] })?.data?.[0];
          if (found?.id == null) {
            grid.handleError('no_record_selected');
            return;
          }
          void this.router.navigate([route], {
            queryParams: { id: String(found.id), mode: 'view', moduleType },
          });
        },
        error: (err) => grid.handleError(err),
      });
    return true;
  }
}
